import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { SharedService } from './shared.service';
import { AuthService } from './auth.service';
import { BehaviorSubject, Observable, from, timer, throwError } from 'rxjs';
import { switchMap, map, tap, retry } from 'rxjs/operators';
import { Constants } from '../constants';
import { AllNewsResult, VeNewsFeed } from '../typings';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class HomeService implements OnDestroy {

    private readonly MAX_RETRIES = 3;
    private hasPendingRetry = false;
    private readonly onlineHandler = () => {
        if (this.hasPendingRetry) {
            console.log('HomeService: network restored, retrying loadNews...');
            this.hasPendingRetry = false;
            this.loadNews();
        }
    };

    constructor(private http: HttpClient, private sharedService: SharedService, private authService: AuthService) {
        window.addEventListener('online', this.onlineHandler);
    }

    ngOnDestroy(): void {
        window.removeEventListener('online', this.onlineHandler);
    }
    public allNews = new BehaviorSubject<AllNewsResult|null>(null);
    private currentNewsIndex = new BehaviorSubject<number>(0);
    private currentNews = new BehaviorSubject<VeNewsFeed|null>(null);
    private needToReloadNews = new BehaviorSubject<boolean>(false);
    public readonly uuid = uuidv4();
    private DefaultLocalNews: VeNewsFeed = {
            title: 'LOCAL.TITLE',
            url: '',
            description: '',
            summary: '',
            content: '',
            buttonText: '',
            date: new Date(),
            channel: '',
            image: ['../assets/logo/local_thumbnail.png'],
            video: '',
            type: '',
            subType: '',
            state: '',
            authorName: '',
            authorImage: '',
            contentUrl: '',
            language: '',
            id: '0',
            videoUrl: ''
        }

    private processNewsForLocal(news: AllNewsResult): AllNewsResult {
        if (news?.localNews != null && news?.localNews.length > 0) {
            const modifiedNews: VeNewsFeed[] = [];
            let atLeastOneDefaultNews = false;
            console.log(news);
            if (news.internalNews && news.internalNews.length > 0) {
                news.internalNews.forEach((item, index) => {
                    modifiedNews.push(item);
                    if ((index + 1) % 5 === 0) {
                        modifiedNews.push(this.DefaultLocalNews);
                        atLeastOneDefaultNews = true;
                    }
                });
                if (!atLeastOneDefaultNews) {
                    modifiedNews.push(this.DefaultLocalNews);
                }
            }
            news.internalNews = modifiedNews;
        }
        return news;
    }

    public loadNews(): void {
        from(this.sharedService.getToken()).pipe(
            switchMap(token => {
                const params: any = { 'encryptedToken': token, 'uuid': this.uuid };
                return this.http.get<AllNewsResult>(`${Constants.Api.VeNewsFeed}`, { params });
            }),
            map(news => this.processNewsForLocal(news)),
            retry({
                count: this.MAX_RETRIES,
                delay: (error: HttpErrorResponse, retryCount) => {
                    if (error.status === 401 || error.status === 500) {
                        return throwError(() => error);
                    }
                    return timer(Math.min(1000 * Math.pow(2, retryCount), 30000));
                }
            })
        ).subscribe({
            next: news => {
                this.hasPendingRetry = false;
                this.allNews.next(news);
            },
            error: (error: HttpErrorResponse) => {
                if (error.status === 401) {
                    this.hasPendingRetry = false;
                    this.sharedService.navigate([Constants.RedirectUrl.Auth]);
                    return;
                }
                if (error.status === 500) {
                    this.hasPendingRetry = false;
                    this.sharedService.navigate([Constants.RedirectUrl.Error]);
                    return;
                }
                this.hasPendingRetry = true;
                console.warn('HomeService: loadNews failed after retries, waiting for network to restore...');
            }
        });
    }

    /**
     * Recharge les news et retourne un Observable.
     * Utiliser pour les rechargements en cours de cycle (fin de playlist).
     * Émet également sur allNews pour maintenir le cache à jour.
     */
    public reloadNewsData(): Observable<AllNewsResult> {
        return from(this.sharedService.getToken()).pipe(
            switchMap(token => {
                const params: any = { 'encryptedToken': token, 'uuid': this.uuid };
                return this.http.get<AllNewsResult>(`${Constants.Api.VeNewsFeed}`, { params });
            }),
            map(news => this.processNewsForLocal(news)),
            tap(news => this.allNews.next(news))
        );
    }

    public setCurrentNews(news: VeNewsFeed): void {
        this.currentNews.next(news);
    }

    public setNewsIndex(index: number): void {
        this.currentNewsIndex.next(index);
    }

    public getNewsIndex(): number {
        return this.currentNewsIndex.getValue();
    }

    public getCurrentNews(): VeNewsFeed|null {
        return this.currentNews.getValue();
    }

    public getNews(): Observable<AllNewsResult|null> {
        return this.allNews.asObservable();
    }

    public setNeedToReloadNews(needToReload: boolean): void {
        this.needToReloadNews.next(needToReload);
    }

    public getNeedToReloadNews(): boolean {
        return this.needToReloadNews.getValue();
    }
}
